import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { chatbotsApi } from "@/api/endpoints/chatbots";
import { sitesApi } from "@/api/endpoints/sites";
import { tenantsApi } from "@/api/endpoints/tenants";
import { Modal } from "@/components/Modal";
import { FormError } from "@/components/FormError";
import { Icon } from "@/components/Icon";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { canManageContent, permissionTooltip } from "@/lib/rbac";
import { EmptyState } from "@/components/EmptyState";

const createSchema = z.object({
  name: z.string().trim().min(1, "Name is required.").max(80, "Name too long."),
  site_id: z.string().min(1, "Choose a site."),
});
type CreateValues = z.infer<typeof createSchema>;

export function ChatbotsListPage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const currentQuery = useQuery({
    queryKey: ["tenants", "current"],
    queryFn: () => tenantsApi.current(),
  });
  const role = currentQuery.data?.role ?? "viewer";
  const canEdit = canManageContent(role);

  const sitesQuery = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.list(),
  });
  const chatbotsQuery = useQuery({
    queryKey: ["chatbots"],
    queryFn: () => chatbotsApi.list(),
  });

  const sitesById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sitesQuery.data?.sites ?? []) m.set(s.id, s.domain);
    return m;
  }, [sitesQuery.data]);

  const form = useForm<CreateValues>({ defaultValues: { name: "", site_id: "" } });

  const createMutation = useMutation({
    mutationFn: (v: CreateValues) => chatbotsApi.create(createSchema.parse(v)),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["chatbots"] });
      setCreateOpen(false);
      form.reset();
      toast.push("success", "Chatbot created.");
      navigate({ to: "/chatbots/$id", params: { id: res.chatbot.id } });
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const sites = sitesQuery.data?.sites ?? [];
  const noSites = sites.length === 0;

  return (
    <div className="page" data-testid="chatbots-list-page">
      <div className="page-head">
        <div>
          <div className="page-title">Chatbots</div>
          <div className="page-sub">
            One AI assistant per site. Configure the widget, knowledge and
            prompts from the chatbot detail view.
          </div>
        </div>
        {canEdit ? (
          <div className="page-actions">
            <button
              className="btn btn-accent btn-sm"
              onClick={() => setCreateOpen(true)}
              disabled={noSites}
              title={noSites ? "Register a site first" : ""}
              data-testid="open-create-chatbot"
            >
              <Icon name="plus" size={12} /> New chatbot
            </button>
          </div>
        ) : null}
      </div>

      <div className="card">
        <div
          className="table-row-head"
          style={{ gridTemplateColumns: "1.6fr 1.6fr 1fr 110px 140px" }}
        >
          <div>Name</div>
          <div>Site</div>
          <div>Status</div>
          <div>Default</div>
          <div>Created</div>
        </div>
        {chatbotsQuery.data?.chatbots.map((c) => (
          <Link
            key={c.id}
            to="/chatbots/$id"
            params={{ id: c.id }}
            className="list-row"
            style={{
              gridTemplateColumns: "1.6fr 1.6fr 1fr 110px 140px",
              textDecoration: "none",
              color: "inherit",
            }}
            data-testid={`chatbot-row-${c.slug}`}
          >
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{c.name}</div>
              <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                {c.slug}
              </div>
            </div>
            <div className="mono" style={{ fontSize: 12 }}>
              {sitesById.get(c.site_id) ?? "—"}
            </div>
            <div>
              <span
                className={"chip " + (c.status === "active" ? "chip-success" : "chip-warning")}
              >
                {c.status}
              </span>
            </div>
            <div>
              {c.is_default ? <span className="chip chip-accent">Default</span> : null}
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
              {new Date(c.created_at).toLocaleDateString()}
            </div>
          </Link>
        ))}
        {(chatbotsQuery.data?.chatbots.length ?? 0) === 0 ? (
          <EmptyState
            icon="bot"
            title="No chatbots yet"
            description={
              canEdit
                ? noSites
                  ? "Register a site first — your chatbot is anchored to a domain so the embed snippet knows where to load."
                  : "Pick a site and give your chatbot a friendly name. We'll generate the embed snippet automatically."
                : "Ask an Owner or Admin to create one for this workspace."
            }
            primary={
              canEdit && !noSites
                ? {
                    label: "New chatbot",
                    onClick: () => setCreateOpen(true),
                    testId: "empty-chatbots-add",
                  }
                : undefined
            }
            secondary={
              canEdit && noSites
                ? { label: "Add a site first", href: "/sites", testId: "empty-chatbots-go-sites" }
                : undefined
            }
            testId="empty-chatbots"
          />
        ) : null}
      </div>

      <Modal
        title="Create chatbot"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => setCreateOpen(false)}>
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={createMutation.isPending}
              onClick={form.handleSubmit((v) => createMutation.mutate(v))}
              data-testid="submit-create-chatbot"
            >
              {createMutation.isPending ? "Creating…" : "Create chatbot"}
            </button>
          </>
        }
      >
        <div className="field-group">
          <label>Name</label>
          <input
            className="field-input"
            placeholder="Front desk assistant"
            data-testid="chatbot-name"
            {...form.register("name")}
          />
          <FormError message={form.formState.errors.name?.message} />
        </div>
        <div className="field-group">
          <label>Site</label>
          <select
            className="field-input"
            data-testid="chatbot-site"
            {...form.register("site_id")}
            disabled={noSites}
          >
            <option value="">Select a site</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · {s.domain}
              </option>
            ))}
          </select>
          <FormError message={form.formState.errors.site_id?.message} />
          {noSites ? (
            <div className="help" style={{ color: "var(--text-3)" }}>
              No sites registered yet. Add one in <Link to="/sites">Sites</Link>.
            </div>
          ) : null}
        </div>
      </Modal>

      {!canEdit ? (
        <div className="help" style={{ marginTop: 12 }}>
          {permissionTooltip("write")} to create or change chatbots.
        </div>
      ) : null}
    </div>
  );
}
